/**
 * publicTrackingApi — /api/public/etkilesim* endpoint client.
 *
 * Risk Analizi örnekleri, Saha Gözlem örnekleri ve /araclar public araçları
 * için ortak fire-and-forget tracking + count fetch.
 *
 * Anonim — auth header gönderilmez. `keepalive: true` ile page navigation
 * sırasında bile request tamamlanır. Tüm hatalar yutulur — UI'yı bloklamaz.
 */

import type { PublicTrackingTip, PublicSlugCounts } from '@/shared/types/publicTracking';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

/**
 * Fire-and-forget public tracking call.
 *
 * - SSR-safe: window kontrolü yapar
 * - keepalive: page navigation sırasında bile teslim edilir
 * - Sessiz: hata UI'yı bloklamaz
 */
export function trackPublicEvent(slug: string, tip: PublicTrackingTip): void {
  if (typeof window === 'undefined') return;

  try {
    fetch(`${API_URL}/api/public/etkilesim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug,
        tip,
        // pageUrl: cross-origin fetch'te Referer header origin-only kesilir
        pageUrl: window.location.href,
        // sourceReferer: önceki sayfa (Google, sosyal medya — gerçek acquisition kaynağı)
        sourceReferer: document.referrer || null
      }),
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {
      // Sessiz başarısızlık
    });
  } catch {
    // Network/CORS — yutulur
  }
}

/**
 * Tüm slug'lar için aggregate counts (Hub ISR refresh).
 * @returns Map<slug, PublicSlugCounts> — backend down ise boş object
 */
export async function getAllPublicCounts(): Promise<Record<string, PublicSlugCounts>> {
  try {
    const res = await fetch(`${API_URL}/api/public/etkilesim/counts`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      // SSG/ISR — Next 13+ fetch cache; 1 saat TTL
      next: { revalidate: 3600 }
    } as RequestInit & { next?: { revalidate: number } });

    if (!res.ok) return {};

    const data = (await res.json()) as { counts?: Record<string, PublicSlugCounts> };
    return data.counts ?? {};
  } catch {
    return {};
  }
}
